import { Box } from '@mui/material';
import React from 'react';
import { IssueReportRecord } from '../../redux/features/issueReport/issueReport.props';
import { UNCLASSIFIED_ISSUE_TYPE } from '../../constants/issueTypes';

/**
 * HITL-1501: the full record of what has already been flagged this session.
 *
 * ONE component, rendered as the `title` of two different tooltips — the panel's
 * summary line and the Issue button. They must say the same thing: the panel's line
 * truncates to three types to protect panel height, so if the two surfaces diverged
 * the agent would get a different answer depending on where they happened to hover.
 *
 * COMPLETE, deliberately. The line's "+N more" always resolves here, because the
 * reason the line truncates — vertical space inside a panel that must never scroll —
 * does not apply to a floating tooltip.
 *
 * READ-ONLY. Nothing here is clickable: re-classifying an earlier report is out of
 * scope (the flag window is the only classification surface), and an interactive
 * tooltip would also start swallowing the mousedowns the agent-interception guards
 * are watching for.
 */

/**
 * Coarse on purpose — "2m ago", not "2m 14s ago". The agent is answering "did I
 * already report this?", where the useful resolution is which part of this order it
 * happened in. Seconds below a minute because the first minute is where duplicate
 * presses actually cluster.
 */
export const formatFlagAge = (createdAt: string, nowMs: number): string => {
  const elapsedMs = nowMs - Date.parse(createdAt);
  // A record stamped in the future is a clock skew, not a negative age.
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return 'just now';
  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
};

interface PriorFlagsTooltipProps {
  /** Newest first — the order they are rendered in. */
  reports: IssueReportRecord[];
  /**
   * Injected rather than read from `Date.now()` at render time so the ages are
   * stable within one open tooltip and testable without fake timers.
   */
  nowMs: number;
}

const PriorFlagsTooltip = ({ reports, nowMs }: PriorFlagsTooltipProps) => {
  if (!reports.length) return null;

  return (
    <Box data-issue-report="true" sx={{ py: 0.25 }}>
      {reports.map((report) => (
        <Box
          key={report.issue_id}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            py: '4px',
            fontSize: '0.78rem',
            lineHeight: 1.4,
          }}
        >
          <Box
            component="span"
            sx={{
              flex: '0 0 auto',
              // An unclassified report is the absence of a choice, so it is set apart
              // rather than reading as a type called "Unclassified".
              color: report.issue_type ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
              fontWeight: report.issue_type ? 500 : 400,
              fontStyle: report.issue_type ? 'normal' : 'italic',
            }}
          >
            {report.issue_type ?? UNCLASSIFIED_ISSUE_TYPE}
          </Box>

          <Box
            component="span"
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              color: report.issue_detail
                ? 'rgba(255,255,255,0.68)'
                : 'rgba(255,255,255,0.34)',
              // ISSUE_DETAIL_CHAR_CAP is 500, so a long detail has to wrap and be
              // bounded rather than run the tooltip off the screen.
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {report.issue_detail ?? 'no detail'}
          </Box>

          <Box
            component="span"
            sx={{
              flex: '0 0 auto',
              color: 'rgba(255,255,255,0.45)',
              fontSize: '0.72rem',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatFlagAge(report.created_at, nowMs)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default PriorFlagsTooltip;
